import * as React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid2";
import { FormInputText } from "../common/FormInputText";
import { useForm } from "react-hook-form";
import { loginSchema } from "../../utilities/schema";
import { yupResolver } from "@hookform/resolvers/yup";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useAuth } from "../../hooks/AuthContext";
import UtsavPackLogo from "../../assets/images/utsav-pack-logo.png";

import { useEffect } from "react";
import { useCallback } from "react";
import OTPForm from "./OTPForm";
import { auth } from "../../../firebase";
import { AnimatePresence, motion } from "framer-motion";
import { useRef } from "react";
import { Close } from "@mui/icons-material";

const WelcomeScreen = React.memo(({ handleClose }) => (
  <Grid
    container
    spacing={2}
    sx={{
      p: 4,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "320px",
    }}
  >
    <Grid
      item
      xs={12}
      sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
    >
      <Box
        component="img"
        src={UtsavPackLogo}
        alt="Utsav Pack"
        sx={{
          maxWidth: "160px",
          animation: "glowPulse 3s ease-in-out infinite",
          "@keyframes glowPulse": {
            "0%": { filter: "drop-shadow(0px 0px 0px rgba(196, 154, 39, 0.4))" },
            "50%": { filter: "drop-shadow(0px 0px 10px rgba(196, 154, 39, 0.7))" },
            "100%": { filter: "drop-shadow(0px 0px 0px rgba(196, 154, 39, 0.4))" },
          },
        }}
      />
    </Grid>
    <Grid item xs={12} sx={{ display: "flex", justifyContent: "center", flexDirection: "column", width: "100%", gap: 1 }}>
      <Typography variant="h3" sx={{ fontWeight: 700, fontSize: 24, textAlign: "center", color: "var(--neutral-black)" }}>
        You're all set!
      </Typography>
      <Typography variant="body1" sx={{ fontSize: 15, textAlign: "center", color: "var(--neutral-700)" }}>
        Welcome to Utsav Pack - your packaging partner.
      </Typography>
    </Grid>
    <Grid item xs={12} sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
      <Button variant="contained" onClick={handleClose} size="small" sx={{ px: 5, py: 1 }}>
        Start Exploring
      </Button>
    </Grid>
  </Grid>
));

export default function LoginDialog() {
  const { user, setUser, loginDialogOpen, setLoginDialogOpen } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const methods = useForm({
    resolver: yupResolver(loginSchema),
    // Validation only happens on submit, not on every keystroke
  });
  const {
    handleSubmit,
    reset,
    control,
    watch,
    register,
    formState: { errors, isValid },
  } = methods;
  const [otpForm, setOtpForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState(null);
  const [maxWidth, setMaxWidth] = useState("md");
  const [hideImage, setHideImage] = useState(false);

  const [showWelcomeScreen, setShowWelcomeScreen] = useState(false);
  const [isRecaptchaReady, setIsRecaptchaReady] = useState(false);

  const initializeRecaptcha = useCallback(() => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(
        auth,
        "recaptcha-container",
        {
          size: "invisible",
          callback: () => setIsRecaptchaReady(true),
          "expired-callback": () => setIsRecaptchaReady(false),
        }
      );
    }
  }, [auth]);

  useEffect(() => {
    if (document.getElementById("recaptcha-container")) {
      initializeRecaptcha();
    }
    return () => {
      try {
        window.recaptchaVerifier?.clear();
        window.recaptchaVerifier = null;
      } catch (e) {
        console.error("Cleanup error:", e);
      }
    };
  }, [initializeRecaptcha]);

  const phoneAuth = async (data) => {
    try {
      setLoading(true);
      const phoneNumber = `+91${data?.mobile}`;

      console.log("Starting phone authentication for:", phoneNumber);

      // Get the recaptchaVerifier
      const appVerifier = window.recaptchaVerifier;

      if (!appVerifier) {
        console.error("reCAPTCHA verifier not initialized");
        return;
      }

      // Send the verification code
      const confirmationResult = await signInWithPhoneNumber(
        auth,
        phoneNumber,
        appVerifier
      );

      // Store the confirmation result for later verification
      window.confirmationResult = confirmationResult;
      console.log("SMS sent successfully");
      setOtpForm(true);
      setLoginData(data);
      setLoading(false);
      // Show OTP input field
      // ...
    } catch (error) {
      console.error("Error sending OTP:", error);

      // Reset the reCAPTCHA on error
      try {
        if (window.recaptchaWidgetId) {
          grecaptcha.reset(window.recaptchaWidgetId);
        } else {
          // If you haven't stored the widget ID
          window.recaptchaVerifier.render().then(function (widgetId) {
            window.recaptchaWidgetId = widgetId;
            grecaptcha.reset(widgetId);
          });
        }
      } catch (e) {
        console.log("Error resetting reCAPTCHA:", e);
      }
    } finally {
      setLoading(false);
    }
  };

  const cancelHandler = (fromOtpFlag = false) => {
    reset();
    setOtpForm(false);
    setLoginData(null);
    !fromOtpFlag && handleClose();
    setShowWelcomeScreen(fromOtpFlag);
    setLoading(false);
  };
  const handleClose = () => {
    setLoginDialogOpen(false);
  };

  const handleResendOtp = useCallback(() => {
    setTimer(30);
    setResendDisabled(true);
    phoneAuth(loginData);
  }, []);

  const LoginAndOTPForm = () => {
    return (
      <Grid container spacing={0} sx={{ minHeight: 520, background: "#F9F5EF" }}>
        <Grid
          size={{ xs: 12, sm: 5 }}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            background: "linear-gradient(180deg, #FFFDF8 0%, #F3E7D2 100%)",
            p: 4,
            gap: 3,
            minHeight: "420px",
            position: "relative",
            overflow: "hidden",
            borderRight: "1px solid rgba(146, 109, 33, 0.12)",
          }}
        >
          <Box sx={{ position: "absolute", inset: 0, background: "radial-gradient(circle at top left, rgba(196,154,39,0.1), transparent 42%)" }} />
          <Box sx={{ position: "absolute", top: 24, left: 24, width: 46, height: 46, border: "1px solid rgba(169, 120, 20, 0.35)", borderRadius: "10px", background: "rgba(255,255,255,0.2)" }} />
          <Box sx={{ position: "absolute", bottom: 28, right: 28, width: 54, height: 54, border: "1px solid rgba(169,120,20,0.25)", borderRadius: "12px", background: "rgba(255,255,255,0.15)" }} />

          <Box component="img" src={UtsavPackLogo} alt="Utsav Pack" sx={{ maxWidth: "150px", position: "relative", zIndex: 1 }} />

          <Box sx={{ position: "relative", zIndex: 1, textAlign: "center", maxWidth: 260 }}>
            <Typography sx={{ color: "#1A1A1A", fontSize: "30px", fontWeight: 800, letterSpacing: "-0.04em", lineHeight: 1.1 }}>
              Utsav Pack
            </Typography>
            <Typography sx={{ mt: 1.5, color: "#4A463E", fontSize: "15px", fontStyle: "italic", lineHeight: 1.5 }}>
              Premium packaging, printing & custom solutions.
            </Typography>
          </Box>

          <Box sx={{ position: "relative", zIndex: 1, border: "1px solid rgba(169,120,20,0.35)", borderRadius: "999px", px: 2.2, py: 0.8, background: "rgba(255,255,255,0.28)", color: "#7A4F00", fontSize: "10px", letterSpacing: "0.16em", fontWeight: 800, textTransform: "uppercase" }}>
            Packaging • Printing • Possibilities
          </Box>
        </Grid>

        <Grid
          size={{ xs: 12, sm: 7 }}
          sx={{ display: "flex", flexDirection: "column", background: "#FFFDFC" }}
        >
          <Grid container sx={{ px: { xs: 3, md: 4 }, py: { xs: 3, md: 4 }, m: "auto", width: "100%" }} spacing={2.5}>
            <Grid size={12} sx={{ display: "flex", justifyContent: "center", gap: 1.5, flexDirection: "column" }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "24px", md: "30px" },
                  lineHeight: 1.2,
                  textAlign: "center",
                  color: "#1D1D1B",
                  letterSpacing: "-0.04em",
                }}
              >
                {!otpForm ? "Log in / Sign Up" : "OTP Verification"}
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  fontWeight: 500,
                  fontSize: "15px",
                  lineHeight: 1.6,
                  textAlign: "center",
                  color: "#473F36",
                }}
              >
                {!otpForm ? (
                  "Access premium packaging, custom orders, and brand-ready solutions."
                ) : (
                  <>
                    We have sent an OTP to your mobile number -{" "}
                    <Typography
                      component="span"
                      sx={{
                        fontWeight: 700,
                        fontSize: "15px",
                        lineHeight: 1.6,
                        textAlign: "center",
                        color: "#1D1D1B",
                      }}
                    >
                      +91 {loginData?.mobile}
                    </Typography>
                  </>
                )}
              </Typography>
            </Grid>

            {!otpForm ? (
              <>
                <Grid size={12}>
                  <FormInputText
                    name="name"
                    control={control}
                    label="User Name"
                    errors={errors}
                    type="text"
                  />
                </Grid>
                <Grid size={12}>
                  <FormInputText
                    name="mobile"
                    control={control}
                    label="Mobile number"
                    errors={errors}
                    type="number"
                    maxLength={10}
                    numberString={true}
                  />
                </Grid>

                <Grid size={12}>
                  <Button
                    data-testid="get-otp-btn"
                    sx={{
                      width: "100%",
                      height: "52px",
                      borderRadius: "14px",
                      background: "linear-gradient(135deg, #C49A27 0%, #D6AE4A 100%)",
                      color: "#fff",
                      fontWeight: 800,
                      fontSize: "16px",
                      letterSpacing: "0.01em",
                      boxShadow: "0 14px 26px rgba(196,154,39,0.22)",
                      textTransform: "none",
                      "&:hover": { background: "linear-gradient(135deg, #B98917 0%, #C89F36 100%)" },
                    }}
                    variant="contained"
                    onClick={handleSubmit(phoneAuth)}
                    id="sign-out-button"
                  >
                    {loading ? (
                      <CircularProgress color="white" size="21px" />
                    ) : (
                      "Get OTP"
                    )}
                  </Button>
                </Grid>
              </>
            ) : (
              <OTPForm
                handleResendOtp={handleResendOtp}
                cancelHandler={cancelHandler}
                loginData={loginData}
                setShowWelcomeScreen={setShowWelcomeScreen}
                otpForm={otpForm}
              />
            )}
          </Grid>
        </Grid>
      </Grid>
    );
  };
  const LoginAndOTPFormForMobile = () => {
    return (
      <>
        <IconButton
          edge="start"
          color="inherit"
          onClick={handleClose}
          aria-label="close"
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            zIndex: 1000,
            color: "var(--neutral-800)",
            backgroundColor: "rgba(255,255,255,0.8)",
            backdropFilter: "blur(8px)",
            border: "1px solid rgba(146,109,33,0.18)",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.96)",
            },
          }}
        >
          <Close />
        </IconButton>
        <Grid container spacing={0} sx={{ background: "#FFFDF9" }}>
          <motion.div style={{ width: "100%" }}>
            <Grid container spacing={0}>
              <Grid
                size={{ xs: 12 }}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "linear-gradient(180deg, #FFFDF8 0%, #F3E7D2 100%)",
                  py: 4,
                  px: 3,
                  gap: 2,
                  minHeight: "210px",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <Box sx={{ position: "absolute", top: 16, left: 16, width: 34, height: 34, border: "1px solid rgba(169,120,20,0.35)", borderRadius: "10px" }} />
                <Box sx={{ position: "absolute", bottom: 18, right: 18, width: 42, height: 42, border: "1px solid rgba(169,120,20,0.25)", borderRadius: "12px" }} />

                <Box component="img" src={UtsavPackLogo} alt="Utsav Pack" sx={{ maxWidth: "120px" }} />
                <Typography sx={{ color: "#403B35", fontSize: "13px", fontStyle: "italic", textAlign: "center", lineHeight: 1.5 }}>
                  Premium packaging, printing & custom solutions.
                </Typography>
                <Box sx={{ border: "1px solid rgba(169,120,20,0.35)", borderRadius: "999px", px: 1.5, py: 0.5, color: "#7A4F00", fontSize: "8px", letterSpacing: "0.15em", fontWeight: 800, textTransform: "uppercase" }}>
                  Packaging • Printing • Possibilities
                </Box>
              </Grid>
              <Grid
                size={{ xs: 12 }}
                sx={{ display: "flex", flexDirection: "column", background: "#FFFDFC" }}
              >
                <Grid
                  container
                  sx={{ px: 3, py: 3, m: "auto", width: "100%" }}
                  spacing={2.5}
                >
                  <Grid
                    size={12}
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 1.5,
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      id="login-dialog-title"
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        fontSize: "24px",
                        lineHeight: 1.2,
                        textAlign: "center",
                        color: "#1D1D1B",
                        letterSpacing: "-0.04em",
                      }}
                    >
                      {!otpForm ? "Log in / Sign Up" : "OTP Verification"}
                    </Typography>

                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        fontSize: "14px",
                        lineHeight: 1.6,
                        textAlign: "center",
                        color: "#473F36",
                      }}
                    >
                      {!otpForm ? (
                        "Access premium packaging, custom orders, and brand-ready solutions."
                      ) : (
                        <>
                          We have sent an OTP to your mobile number -{" "}
                          <Typography
                            component="span"
                            sx={{
                              fontWeight: 700,
                              fontSize: "14px",
                              lineHeight: 1.6,
                              textAlign: "center",
                              color: "#1D1D1B",
                            }}
                          >
                            +91 {loginData?.mobile}
                          </Typography>
                        </>
                      )}
                    </Typography>
                  </Grid>

                  {!otpForm ? (
                    <>
                      <Grid size={12}>
                        <FormInputText
                          name="name"
                          control={control}
                          label="User Name"
                          errors={errors}
                          type="text"
                        />
                      </Grid>
                      <Grid size={12}>
                        <FormInputText
                          name="mobile"
                          control={control}
                          label="Mobile number"
                          errors={errors}
                          type="number"
                          maxLength={10}
                          numberString={true}
                        />
                      </Grid>
                      <Grid size={12}>
                        <Button
                          data-testid="get-otp-btn"
                          sx={{
                            width: "100%",
                            height: "50px",
                            borderRadius: "14px",
                            background: "linear-gradient(135deg, #C49A27 0%, #D6AE4A 100%)",
                            color: "#fff",
                            fontWeight: 800,
                            fontSize: "15px",
                            letterSpacing: "0.01em",
                            boxShadow: "0 12px 22px rgba(196,154,39,0.22)",
                            textTransform: "none",
                            "&:hover": { background: "linear-gradient(135deg, #B98917 0%, #C89F36 100%)" },
                          }}
                          variant="contained"
                          onClick={handleSubmit(phoneAuth)}
                          id="sign-out-button"
                        >
                          {loading ? (
                            <CircularProgress color="white" size="21px" />
                          ) : (
                            "Get OTP"
                          )}
                        </Button>
                      </Grid>
                    </>
                  ) : (
                    <OTPForm
                      handleResendOtp={handleResendOtp}
                      cancelHandler={cancelHandler}
                      loginData={loginData}
                      setShowWelcomeScreen={setShowWelcomeScreen}
                      otpForm={otpForm}
                    />
                  )}
                </Grid>
              </Grid>
            </Grid>
          </motion.div>
        </Grid>
      </>
    );
  };
  useEffect(() => {
    if (loginDialogOpen) {
      setShowWelcomeScreen(false);
      setOtpForm(false);
      setLoginData(null);
      reset();
    }
  }, [loginDialogOpen]);
  const centerContentStyle = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  };
  return (
    <React.Fragment>
      <div id="recaptcha-container"></div>

      <Dialog
        fullWidth={true}
        maxWidth={maxWidth}
        open={loginDialogOpen}
        onClose={handleClose}
        fullScreen={isMobile}
        aria-labelledby="login-dialog-title"
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: "28px" },
            overflow: "hidden",
            boxShadow: "0 26px 60px rgba(19, 18, 15, 0.12)",
            background: "#FFFDFC",
          },
        }}
      >
        <DialogContent
          sx={{
            padding: 0,
            background: "#FFFDFC",
            ...(isMobile && centerContentStyle),
          }}
        >
          {!showWelcomeScreen ? (
            isMobile ? (
              <LoginAndOTPFormForMobile />
            ) : (
              <LoginAndOTPForm />
            )
          ) : (
            <>
              <WelcomeScreen handleClose={handleClose} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
}
